import Router from 'next/router';
import NProgress from 'nprogress';
import { UAParser } from 'ua-parser-js';
import { AnyAction } from 'redux';
import { ofType } from 'redux-observable';
import { Observable, from, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  throttleTime,
  withLatestFrom,
} from 'rxjs/operators';
import { Countries } from '@bridebook/models';
import { PartialRecursive } from '@bridebook/models/source/abstract/_';
import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import { getWindowSizes } from '@bridebook/web/app-shared/lib/app/utils/get-window-sizes';
import { getHtmlLangAttribute } from '@bridebook/web/lib/app/utils/get-html-lang-attribute';
import { AuthActionTypes } from '@bridebook/web/lib/auth/action-types';
import { ChecklistActionTypes } from '@bridebook/web/lib/checklist/action-types';
import { GuestlistActionTypes } from '@bridebook/web/lib/guestlist/action-types';
import { TaskActionTypes } from '@bridebook/web/lib/task/action-types';
import { Action, IApplicationState, IEpic, IEpicDeps } from '@bridebook/web/lib/types';
import { getAsPathNormalized, getAsPathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { WeddingWebsiteActionTypes } from '@bridebook/web/lib/wedding-website/action-types';
import { WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
import { updateWeddingField } from '../weddings/actions';
import { isAgentAction } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';
import { withPlannerSource } from '@bridebook/web/lib/ai-planner/analytics/planner-source-url';
import { inheritActionMeta } from '@bridebook/web/lib/epics/inherit-action-meta';
import {
  appError,
  fetchGroupsSuccess,
  navigateToError,
  navigateToSuccess,
  resizeWindow,
  routeChangeComplete,
  routeChangeStart,
  setDevice,
} from './actions';

export const routingEpic = (action$: Observable<Action>): Observable<any> => {
  const routerObserver$ = () =>
    new Observable<ReturnType<typeof routeChangeComplete> | ReturnType<typeof routeChangeStart>>(
      (observer) => {
        Router.events.on('routeChangeComplete', () => {
          NProgress.done();

          // Next Router overrides our lang setting from _document.tsx, so we need to override it again
          // @see: https://github.com/vercel/next.js/issues/18452#issuecomment-943467729
          if (document) {
            document.documentElement.lang = getHtmlLangAttribute(Router.locale);
          }

          const url = getAsPathNormalized();
          observer.next(routeChangeComplete(url, Router.query, Router.pathname));
        });

        Router.events.on('routeChangeStart', (url, { shallow } = {}) => {
          const asPath = getAsPathNormalized();
          const asPathWithoutMarket = getAsPathWithoutMarket();
          observer.next(
            routeChangeStart(asPath, asPathWithoutMarket, Router.query, url, Router.locale),
          );
          if (!shallow) {
            NProgress.start();
          }
        });

        Router.events.on('routeChangeError', () => {
          NProgress.done();
        });
      },
    );
  return action$.pipe(ofType('APP_STARTED'), mergeMap(routerObserver$));
};

export const navigationEpic: IEpic<AnyAction> = (action$) =>
  action$.pipe(
    ofType('NAVIGATE_TO_PATH'),
    mergeMap((trigger) => {
      let { path } = trigger.payload as { path: string };
      const { query } = Router;

      const getPromise = async () => {
        if (!path) {
          return false;
        }

        if (!path.startsWith('/' + query.market))
          path = '/' + query.market + (path.startsWith('/') ? '' : '/') + path;

        // A page the planner opens is marked as its visit. The outcome keeps the plain path.
        await Router.push(isAgentAction(trigger) ? withPlannerSource(path) : path);
        return true;
      };

      // The flow is unchanged; only its outcome is reported now — the AI
      // planner's dispatch awaits it. A rejected push must become the failure
      // follow-up, not an error that unsubscribes the epic and kills
      // navigation for the rest of the session.
      return from(getPromise()).pipe(
        map((pushed) =>
          pushed
            ? navigateToSuccess({ path })
            : navigateToError({ path }, new Error('No path given.')),
        ),
        catchError((error: Error) => of(navigateToError({ path }, error))),
        inheritActionMeta(trigger),
      );
    }),
  );

export const windowResizeEpic = (action$: Observable<Action>, deps: IEpicDeps): Observable<any> => {
  const createResizer$ = () =>
    new Observable<ReturnType<typeof resizeWindow>>((observer) => {
      let resizeTimeout: NodeJS.Timer | null;
      const resizeThrottler = () => {
        if (!resizeTimeout) {
          resizeTimeout = setTimeout(() => {
            resizeTimeout = null;
            const sizes = getWindowSizes();

            observer.next(resizeWindow(sizes));
          }, 500);
        }
      };
      resizeThrottler();
      window.addEventListener('resize', resizeThrottler, false);
    });

  return action$.pipe(
    filter((action) => action.type === 'APP_STARTED'),
    withLatestFrom(deps.state$),
    switchMap(() => createResizer$()),
  );
};

export const windowResizeAndroidScrollToInputEpic = (
  action$: Observable<Action>,
  deps: IEpicDeps,
): Observable<any> => {
  const createResizer$ = (state: IApplicationState) =>
    Observable.create(() => {
      const resizeThrottler = () => {
        const { isAndroid } = state.app.device;

        if (
          isAndroid &&
          (document.activeElement?.tagName === 'INPUT' ||
            document.activeElement?.tagName === 'TEXTAREA')
        ) {
          setTimeout(() => {
            document.activeElement?.scrollIntoView({
              block: 'center',
              inline: 'nearest',
            });
          }, 350);
        }
      };
      resizeThrottler();
      window.addEventListener('resize', resizeThrottler, false);
    });

  return action$.pipe(
    filter((action) => action.type === 'APP_STARTED'),
    withLatestFrom(deps.state$),
    switchMap(([, state]) => createResizer$(state)),
  );
};

export interface QAHelpersWindow extends Window {
  updateDetails: (name: keyof IWedding, value: PartialRecursive<IWedding>) => void;
}

declare let window: QAHelpersWindow;

export const qaHelpersEpic: IEpic<AnyAction, any> = (action$) => {
  const qaHelperInitAction = { type: 'QA_HELPER_INIT' };
  const qaHelper$ = () =>
    new Observable<ReturnType<typeof updateWeddingField> | typeof qaHelperInitAction>(
      (observer) => {
        window.updateDetails = (name: keyof IWedding, value: PartialRecursive<IWedding>) => {
          observer.next(updateWeddingField(name, { [name]: value }));
        };
        observer.next(qaHelperInitAction);
      },
    );
  return action$.pipe(
    ofType('APP_STARTED'),
    switchMap(() => qaHelper$()),
  );
};

export const fetchGroups = (action$: Observable<Action>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType<Action>(WeddingActionTypes.UPDATE_WEDDING, AuthActionTypes.USER_SETUP_COMPLETED),
    withLatestFrom(state$),
    throttleTime(2000),
    mergeMap(([_action, state]: [Action, IApplicationState]) => {
      const { groupsLoaded } = state.app;
      if (groupsLoaded) return of();
      const { country } = state.weddings.profile.l10n;
      const promise = Countries._.getById(country).getGroups();
      return from(promise).pipe(
        mergeMap((payload) => of(fetchGroupsSuccess(payload))),
        catchError((error) => of(appError({ error, feature: 'App fetchGroups' }))),
      );
    }),
  );

const globalListeners = [
  ['INIT_WEDDING_LISTENER', 'STOP_WEDDING_LISTENER'],
  ['INIT_SHORTLIST_LISTENER', 'STOP_SHORTLIST_LISTENER'],
  [WeddingWebsiteActionTypes.INIT_LISTENER, WeddingWebsiteActionTypes.STOP_LISTENER],
  [ChecklistActionTypes.FETCH_TASKS_INITIAL, ChecklistActionTypes.STOP_LISTENER],
  [TaskActionTypes.INIT_TODAY_TASK_LISTENER, TaskActionTypes.STOP_TODAY_TASK_LISTENER],
  [GuestlistActionTypes.INIT_LISTENER, GuestlistActionTypes.STOP_LISTENER],
];

const globalListenersInit = globalListeners.map((action) => ({
  type: action[0],
}));

const globalListenersStop = globalListeners.map((action) => ({
  type: action[1],
}));

export const globalListenersInitEpic = (action$: Observable<Action>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType<Action>(WeddingActionTypes.UPDATE_WEDDING, AuthActionTypes.USER_SETUP_COMPLETED),
    withLatestFrom(state$),
    filter(
      ([, state]: [any, IApplicationState]) =>
        !!state.weddings.profile.id && !state.weddings.listenersInitialised,
    ),
    mergeMap(() =>
      of(
        ...globalListenersInit,
        {
          type: WeddingActionTypes.GLOBAL_LISTENERS_INITIALISED,
        },
        {
          type: WeddingActionTypes.WEDDING_PROFILE_LOADED_ANALYTICS,
        },
      ),
    ),
  );

/**
 * Closes every Firestore listener at the start of the logout flow. This runs
 * before the client is terminated, so no subscription is left pointing at a
 * torn-down connection.
 */
export const globalListenersStopEpic = (action$: Observable<AnyAction>) =>
  action$.pipe(
    ofType(AuthActionTypes.ON_FIREBASE_SIGN_OUT),
    mergeMap(() => of(...globalListenersStop)),
  );

// Client-side counterpart to getInitialDeviceState: ensures osName/osVersion are always populated
// from navigator.userAgent on the client, including on static pages where SSR doesn't run.
// On native, getAppBuildInfoEpic overwrites these with more accurate Capacitor values.
export const initDeviceOsEpic = (action$: Observable<Action>): Observable<any> =>
  action$.pipe(
    ofType('APP_STARTED'),
    mergeMap(() => {
      const uaParserOS = new UAParser(navigator.userAgent).getOS();
      return of(setDevice({ osName: uaParserOS.name ?? '', osVersion: uaParserOS.version ?? '' }));
    }),
  );
